import { useState, useEffect, useContext } from 'react'
import { Box, IconButton, Container } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { useChangeMasterVolume } from '../../../mqtt/mqttHandlerContext'
import Text from '../../../assets/components/Text'
import { useConnectedInstruments } from '../../../hooks/contexts/connectedInstrumentsContext'
import { PolyglotContext } from '../../../polyglot/polyGlotContext'
import { VOLUME_MARKS, VOLUME_MIN, VOLUME_STEP, VOLUME_STEPS } from '../Instruments/Instruments'
import { VolumeIcon } from '../../../components/DynamicVolumeIcon'

interface MasterVolumeProps {
    instrumentVolumes: { [key: string]: number }
    setInstrumentVolumes: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>
    isSmallScreen?: boolean
}

const MasterVolume = ({ setInstrumentVolumes, isSmallScreen = false }: MasterVolumeProps) => {
    const { polyglot } = useContext(PolyglotContext)
    const changeMasterVolume = useChangeMasterVolume()
    const connectedInstruments = useConnectedInstruments()
    const [masterVolume, setMasterVolume] = useState<number>(VOLUME_MARKS[1].value)

    const currentStep = Math.round((masterVolume - VOLUME_MIN) / VOLUME_STEP)

    const handleVolumeStep = (direction: -1 | 1) => {
        const newStep = Math.max(0, Math.min(VOLUME_STEPS - 1, currentStep + direction))

        const newVolume = parseFloat((VOLUME_MIN + newStep * VOLUME_STEP).toFixed(3))

        // Update master volume UI
        setMasterVolume(newVolume)

        // Update every individual instrument UI
        setInstrumentVolumes((prev) => {
            const updated = { ...prev }

            connectedInstruments.forEach((instrument) => {
                updated[instrument.deviceId] = newVolume
            })

            return updated
        })

        // Tell instruments to change volume
        changeMasterVolume({
            volume: newVolume
        })

        localStorage.setItem('masterSliderValue', newVolume.toString())
    }

    const handleMute = () => {
        const newVolume = VOLUME_MIN

        setMasterVolume(newVolume)

        setInstrumentVolumes((prev) => {
            const updated = { ...prev }

            connectedInstruments.forEach((instrument) => {
                updated[instrument.deviceId] = newVolume
            })

            return updated
        })

        changeMasterVolume({ volume: newVolume })
        localStorage.setItem('masterSliderValue', newVolume.toString())
    }

    useEffect(() => {
        const saved = localStorage.getItem('masterSliderValue')
        if (!saved) return

        const savedVolume = parseFloat(saved)
        const savedStep = Math.round((savedVolume - VOLUME_MIN) / VOLUME_STEP)
        const clampedStep = Math.max(0, Math.min(VOLUME_STEPS - 1, savedStep))
        const snappedVolume = parseFloat((VOLUME_MIN + clampedStep * VOLUME_STEP).toFixed(3))

        setMasterVolume(snappedVolume)
    }, [])

    return (
        <Container
            sx={{
                paddingTop: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                }}
            >
                {/* Plus */}
                <IconButton
                    aria-label="Raise master volume"
                    onClick={() => handleVolumeStep(1)}
                    disabled={currentStep === VOLUME_STEPS - 1}
                    sx={{
                        width: 40,
                        height: 40,
                        bgcolor: '#f7f6f4',
                        '&:hover': { bgcolor: '#efeeeb' }
                    }}
                >
                    <AddIcon sx={{ fontSize: 22 }} />
                </IconButton>

                {/* Vertical volume marks */}
                <Box
                    sx={{
                        height: isSmallScreen ? 100 : 140,
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    {VOLUME_MARKS.map((mark, index) => (
                        <Box
                            key={mark.value}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '4px',
                                bgcolor: index <= currentStep ? '#e85e46' : '#deddd9'
                            }}
                        />
                    ))}
                </Box>

                {/* Minus */}
                <IconButton
                    aria-label="Lower master volume"
                    onClick={() => handleVolumeStep(-1)}
                    disabled={currentStep === 0}
                    sx={{
                        width: 40,
                        height: 40,
                        bgcolor: '#f7f6f4',
                        '&:hover': { bgcolor: '#efeeeb' }
                    }}
                >
                    <RemoveIcon sx={{ fontSize: 22 }} />
                </IconButton>

                {/* Volume icon */}
                <IconButton aria-label="Mute master volume" onClick={handleMute} sx={{ width: 40, height: 40 }}>
                    <VolumeIcon value={masterVolume} />
                </IconButton>

                <Text size="standard" textAlign="center">
                    {polyglot.t('home.masterVolume.paragraphMaster')}
                </Text>
            </Box>
        </Container>
    )
}

export default MasterVolume
